import { cn } from "@/lib/utils";
import { ComponentPropsWithoutRef, ElementType, forwardRef } from "react";

// Typography variant mapping to Tailwind classes
const typographyVariants: Record<string, string | Record<string, string>> = {
    h1: "font-['Pretendard'] text-[40px] font-bold leading-[135%]",
    h2: "font-['Pretendard'] text-[28px] font-bold leading-[135%]",
    h3: "font-['Pretendard'] text-[24px] font-bold leading-[135%]",
    h4: "font-['Pretendard'] text-[20px] font-bold leading-[135%]",
    subtitle1: "font-['Pretendard'] text-[18px] font-bold leading-[140%]",
    subtitle2: "font-['Pretendard'] text-base font-semibold leading-[140%]",
    body1: "font-['Pretendard'] text-base font-normal leading-[140%]",
    body2: "font-['Pretendard'] text-sm font-semibold leading-[140%]",
    body3: "font-['Pretendard'] text-sm font-normal leading-[140%]",
    body4: "font-['Pretendard'] text-[13px] font-normal leading-[140%]",
    caption1: "font-['Pretendard'] text-xs font-semibold leading-[140%]",
    caption2: "font-['Pretendard'] text-xs font-normal leading-[140%]",
    button: {
        large: "font-['Pretendard'] text-base font-semibold leading-6",
        medium: "font-['Pretendard'] text-sm font-semibold leading-6",
        small: "font-['Pretendard'] text-xs font-semibold leading-6"
    },
    input: {
        value: "font-['Pretendard'] text-sm font-normal leading-6"
    },
    text: {
        trial: "font-['Pretendard'] text-sm font-semibold leading-[140%] text-trial-main"
    }
};

// Color mapping to Tailwind classes
const colorMap: Record<string, string> = {
    "text.primary": "text-text-primary",
    "text.secondary": "text-text-secondary",
    "text.disabled": "text-text-disabled",
    "text.secondaryWhite": "text-text-secondary-white",
    "text.primaryWhite": "text-text-primary-white",
    "secondary.main": "text-secondary-main",
    "primary.main": "text-primary-main",
    "primary.dark": "text-primary-dark"
};

// Type definitions for variant paths
type TypographyVariant =
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "subtitle1"
    | "subtitle2"
    | "body1"
    | "body2"
    | "body3"
    | "body4"
    | "caption1"
    | "caption2"
    | "button.large"
    | "button.medium"
    | "button.small"
    | "input.value"
    | "text.trial";

type ColorVariant =
    | "text.primary"
    | "text.secondary"
    | "text.disabled"
    | "text.secondaryWhite"
    | "text.primaryWhite"
    | "secondary.main"
    | "primary.main"
    | "primary.dark";

// Helper function to get typography classes from variant path
function getTypographyClasses(variant: TypographyVariant): string {
    if (!variant) return "";
    if (variant.includes(".")) {
        const parts = variant.split(".");
        if (parts.length === 2 && parts[0] && parts[1]) {
            const category = parts[0];
            const size = parts[1];
            const categoryVariants = typographyVariants[category];
            if (
                typeof categoryVariants === "object" &&
                categoryVariants !== null
            ) {
                return (categoryVariants as Record<string, string>)[size] || "";
            }
        }
        return "";
    }
    const variantClass = typographyVariants[variant];
    return typeof variantClass === "string" ? variantClass : "";
}

// Helper function to get color class from color path
function getColorClass(color?: ColorVariant): string {
    if (!color) return "";
    return colorMap[color] || "";
}

export interface TextProps<T extends ElementType = "span"> {
    variant: TypographyVariant;
    color?: ColorVariant;
    tag?: T;
    className?: string;
}

export const Text = forwardRef<
    HTMLElement,
    TextProps<ElementType> &
        Omit<ComponentPropsWithoutRef<ElementType>, "variant" | "tag" | "color">
>(({ variant, tag, color, className, ...props }, ref) => {
    const Tag = (tag || "span") as ElementType;
    const typographyClasses = getTypographyClasses(variant);
    const colorClass = getColorClass(color);

    return (
        <Tag
            ref={ref}
            className={cn(typographyClasses, colorClass, className)}
            {...props}
        />
    );
});

Text.displayName = "Text";
